import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import store from 'state/store'
import { useSplitClient } from '@splitsoftware/splitio-react'
import MarketingPageLayout from 'components/landing-page/layout'
import LandingPage from 'components/landing-page/LandingPage'
import Careers from 'components/landing-page/Careers'
import AboutUs from 'components/landing-page/AboutUs'
import CustomReportBuilderContainer from './components/containers/analytics/CustomReportBuilderContainer.jsx'
import {
  AdjustmentsListContainer,
  AnalyticsContainer,
  AssetDetailContainer,
  AssetHistoryContainer,
  AssetMapContainer,
  BillContainer,
  BillsContainer,
  BulkInvoicePaymentContainer,
  CommoditiesContainer,
  CommoditiesMaterialContainer,
  ContainerAssetsContainer,
  CustomerContactsContainer,
  CustomerContainer,
  CustomerContractsContainer,
  CustomerFinancialInformation,
  CustomerLocationsContainer,
  CustomersContainer,
  CustomerTicketsContainer,
  CustomerVehiclesContainer,
  DashboardContainer,
  DispatchActiveTicketsContainer,
  DispatchTicketContainer,
  DispatchTicketHistoryContainer,
  EditablePurchaseOrderContainer,
  EditableSalesOrderContainer,
  FinishedGoodsListContainer,
  FreightBillsContainer,
  FreightLanesContainer,
  InboundTicketBillContainer,
  InboundTicketContainer,
  InboundTicketsContainer,
  InventoryCommodityContainer,
  InventoryCommodityMaterialContainer,
  InventoryContainer,
  InvoiceContainer,
  InvoiceContainerV2,
  InvoicePaymentsContainer,
  InvoicesContainer,
  MaterialPricingContainer,
  MaterialPropertiesContainer,
  NegativeInventoryListContainer,
  CreateBulkInvoicePaymentContainer,
  OutboundTicketContainer,
  OutboundTicketsContainer,
  PackingListsContainer,
  Page404Container,
  PriceListsContainer,
  ProfileContainer,
  PurchaseOrderContainer,
  PurchaseOrdersContainer,
  RegradesListContainer,
  ReportsContainer,
  SalesOrderContainer,
  SalesOrdersContainer,
  SecurityContainer,
  SettingsContainer,
  SingleReportContainer,
  SyncContainer,
  SyncLogContainer,
  BatchBillsContainer,
  BatchBillContainer,
} from 'components/containers'
// moving away from "barrel" style exports based on Vite framework guidance https://vite.dev/guide/performance.html#avoid-barrel-files
import UsersSettingsContainer from 'components/containers/settings/users/UsersSettingsContainer'
import AppLayout from 'components/layouts/AppLayout'
import ConditionalSalesLayout from 'components/layouts/ConditionalSalesLayout'
import ContentLayout from 'components/layouts/ContentLayout'
import OrganizationsContainer from 'components/containers/admin/organizations/OrganizationsContainer'
import SingleOrgContainer from 'components/containers/admin/organizations/SingleOrgContainer'
import TabsLayout from 'components/layouts/TabsLayout'
import {
  ANALYTICS_ACTIONS,
  ASSETS_ACTIONS,
  COMMODITIES_ACTIONS,
  CONTRACT_ACTIONS,
  CUSTOMER_ACTIONS,
  INVENTORY_ACTIONS,
  LOAD_BOARD_ACTIONS,
  ORGANIZATIONS_ACTIONS,
  PURCHASES_ACTIONS,
  USER_PROVISION_ACTIONS,
} from 'utils/ui/actions'
import {
  ACCOUNT_TAB_ROUTES,
  ANALYTICS_TAB_ROUTES,
  APP_TAB_ROUTES,
  ASSET_DETAIL_TAB_ROUTES,
  ASSET_TAB_ROUTES,
  COMMODITIES_TAB_ROUTES,
  CONTRACT_TAB_ROUTES,
  FREIGHT_TAB_ROUTES,
  INVENTORY_TAB_ROUTES,
  LEDGER_TAB_ROUTES,
  LOAD_BOARD_TAB_ROUTES,
  MATERIAL_PROPERTIES_ROUTES,
  PURCHASES_TAB_ROUTES,
  REPORTS_TAB_ROUTES,
  SALES_TAB_ROUTES,
  SETTINGS_ADMIN_TAB_ROUTES,
  SETTINGS_BILLING_TAB_ROUTES,
  SETTINGS_COMMODITIES_TAB_ROUTES,
  SETTINGS_CONTRACT_TAB_ROUTES,
  SETTINGS_CUSTOMERS_TAB_ROUTES,
  SETTINGS_DISPATCH_TAB_ROUTES,
  SETTINGS_INTEGRATIONS_TAB_ROUTES,
  SETTINGS_INVENTORY_TAB_ROUTES,
  SETTINGS_PURCHASES_TAB_ROUTES,
  SETTINGS_REPORTS_TAB_ROUTES,
  SETTINGS_SALES_TAB_ROUTES,
} from 'utils/ui/routes'
import { withAuthenticator } from '@aws-amplify/ui-react'
import '@aws-amplify/ui-react/styles.css'
import { authModalComponents } from 'auth/authModal'
import TrailerAssetsContainer from 'components/containers/assets/trailers/TrailerAssetsContainer.jsx'
import TruckAssetsContainer from 'components/containers/assets/trucks/TruckAssetsContainer.jsx'
import RouteProtector from 'components/containers/account/RouteProtector'
import { ASSET_CATEGORY, GS_ROLE, ORG_MODE_ROUTES, BILL_TYPES } from 'utils/constants'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import RolesAndPermissionsContainer from 'components/containers/settings/roles-and-permissions/RolesAndPermissionsContainer'
import useRbac from 'utils/custom-hooks/useRbac'

export type AppTab = {
  title: string
  route: string
  treatmentKey?: string
  matchPath?: string
}

const getFilteredSettingsTabs = (treatments: SplitIO.TreatmentsWithConfig | null, tabs: AppTab[]) =>
  tabs?.filter(tab => {
    if (!treatments) return tabs
    const tabTreatment = treatments[tab?.treatmentKey as any]?.treatment
    // if there is a feature flag in the tab we check its on
    return tabTreatment === 'on' || !tabTreatment
  })

/**
 * Generate settings routes
 * if there is a feature flag in the tabs make sure treatments is in params when calling this method
 * @param type
 * @param title
 * @param tabs
 * @param isOrgModeRoute
 * @param treatments
 */
const generateSettingsRoutes = (
  type: string,
  title: string,
  tabs: AppTab[] | null,
  isOrgModeRoute: boolean,
  treatments?: SplitIO.TreatmentsWithConfig,
) => {
  const filteredTabs = tabs ? getFilteredSettingsTabs(treatments || null, tabs) : []
  const hasTabs = filteredTabs.length > 0

  const settingsRouteSharedProps = {
    path: `/${type}`,
    isOrgModeRoute,
    featureFlag: true, // Feature flag is hard-coded as true here because we're already filtering FF'd routes out above (Settings only)
    isSettingsRoute: true,
  }

  if (hasTabs) {
    return (
      <RouteProtector
        {...settingsRouteSharedProps}
        element={
          <ContentLayout title={title}>
            <TabsLayout tabs={filteredTabs} />
          </ContentLayout>
        }
      >
        <Navigate to={filteredTabs[0]?.route} />
        {filteredTabs.map(tab => (
          <Route
            key={`settings-${type}-${tab.title}`}
            path={`/${tab.route}`}
            element={<SettingsContainer layoutType={tab.route} />}
          />
        ))}
      </RouteProtector>
    )
  }

  return (
    <RouteProtector
      {...settingsRouteSharedProps}
      element={
        <ContentLayout title={title}>
          <SettingsContainer layoutType={type} />
        </ContentLayout>
      }
    />
  )
}

const getFilteredTabs = (treatments: SplitIO.TreatmentsWithConfig | null, tabs: AppTab[]) => {
  return tabs.filter(tab => !tab.treatmentKey || treatments?.[tab.treatmentKey]?.treatment === 'on')
}

const AppRoutes = () => {
  const { pathname } = useLocation()
  const {
    treatments,
    variables: {
      appRoutePurchases,
      appRouteSales,
      appRouteContracts,
      appRouteInventory,
      appRouteCustomers,
      appRouteCommodities,
      appRouteFreight,
      appRouteLedger,
      appRouteAnalytics,
      appRouteReports,
      appRouteAssets,
      appRouteLoadBoard,
      enableReportBuilder,
      enableOrgLevelExploReports,
      enableInvoiceContainerV2,
      enableBatchBills,
      enableAutoCloseContracts,
    },
  } = useAppSelector(state => state.featureFlags || {})

  const [customerActions, setCustomerActions] = useState(null)

  // Can't use `useSelector(state => state.ui)` here because it will cause an infinite rerender
  const { ui } = store.getState()
  const { inOrgMode } = ui || {}

  const { erpType } = useAppSelector(state => state.ledger || {})

  useEffect(() => {
    if (pathname?.replace(/\/$/, '')?.split('/')?.pop() === 'customers') {
      setCustomerActions(CUSTOMER_ACTIONS as any)
    } else {
      setCustomerActions(null)
    }
  }, [pathname])

  // both admin and manager have permissions.ALL via legacy /permissions endpoint
  const { roles, isAdmin: isYardAdminOrManager } = useAppSelector(state => state.permissions)
  const yardSettingsLoaded = useAppSelector(state => state.settings.yardSettingsLoaded)
  const { client: splitClient, isReady: splitIsReady } = useSplitClient()
  const isYardAdmin = !!roles?.find(r => r.roleName === GS_ROLE.YARD_ADMIN)
  const { rbacMap, getAuthorizedTabs, getAuthorizedActions } = useRbac()

  type SplitClientPromiseRef = {
    promise: Promise<SplitIO.IBrowserClient> | null
    resolve: ((value: SplitIO.IBrowserClient) => void) | null
  }

  const splitClientPromiseRef = useRef<SplitClientPromiseRef>({ promise: null, resolve: null })

  if (!window.getSplitClient) {
    window.getSplitClient = () => {
      if (splitIsReady) {
        return Promise.resolve(splitClient as SplitIO.IBrowserClient)
      } else {
        return splitClientPromiseRef.current.promise
      }
    }
  }

  useEffect(() => {
    if (!splitClientPromiseRef.current.promise) {
      splitClientPromiseRef.current.promise = new Promise(resolve => {
        splitClientPromiseRef.current.resolve = resolve
      })
    }

    if (splitIsReady && splitClientPromiseRef.current.resolve) {
      splitClientPromiseRef.current.resolve(splitClient as SplitIO.IBrowserClient)
    }
  }, [splitIsReady, splitClient])

  /**
   * determine what tabs should be displayed to user
   */
  const appRoutes = useMemo(() => {
    let routes
    const appIsReady = splitIsReady && yardSettingsLoaded
    if (inOrgMode) {
      if (enableOrgLevelExploReports) {
        routes = APP_TAB_ROUTES(appIsReady, treatments, erpType, rbacMap).filter(route =>
          ORG_MODE_ROUTES.includes(route.title),
        )
      } else {
        routes = APP_TAB_ROUTES(appIsReady, treatments, erpType, rbacMap).filter(
          route => ORG_MODE_ROUTES.includes(route.title) && route.title !== 'Reports',
        )
      }
    } else {
      routes = APP_TAB_ROUTES(appIsReady, treatments, erpType, rbacMap)
    }
    return routes
  }, [inOrgMode, splitIsReady, treatments, erpType, rbacMap, yardSettingsLoaded])

  return (
    <Routes>
      <Route path="login">
        <Navigate to="app/purchases" />
      </Route>

      <Route
        path="app"
        element={withAuthenticator(
          () => (
            <AppLayout
              appBarConfig={{
                showNotifications: false,
                showLocationSelect: true,
                showSettings: isYardAdminOrManager,
                showSupport: true,
                showAccountActions: true,
              }}
              routes={appRoutes}
            />
          ),
          {
            hideSignUp: true,
            components: authModalComponents,
          },
        )({})}
      >
        <Navigate to="dashboard" />

        <Route path="dashboard" element={<DashboardContainer />} />

        <Route
          path="account"
          element={
            <ContentLayout title="Account Profile">
              <TabsLayout tabs={ACCOUNT_TAB_ROUTES} />
            </ContentLayout>
          }
        >
          <Navigate to="profile" />
          <Route path="profile" element={<ProfileContainer />} />
          <Route path="security" element={<SecurityContainer />} />
        </Route>

        <RouteProtector
          path="purchases"
          element={
            <ContentLayout
              actions={getAuthorizedActions(
                PURCHASES_ACTIONS.filter(action =>
                  enableBatchBills ? true : action.id !== 'create-batch-bill-button',
                ),
              )}
              title="Purchases - Ticket Library"
            >
              <TabsLayout
                tabs={getAuthorizedTabs(
                  PURCHASES_TAB_ROUTES.filter(tab => (enableBatchBills ? true : tab.route !== 'batch-bills')),
                )}
              />
            </ContentLayout>
          }
          isOrgModeRoute={false}
          featureFlag={appRoutePurchases}
        >
          <Navigate to={getAuthorizedTabs(PURCHASES_TAB_ROUTES)?.[0]?.route || '/dashboard'} replace />
          <Route path="ticket-library">
            <Route path="/" element={<InboundTicketsContainer />} />
            <Route key="create-inbound-ticket" path="new" element={<InboundTicketContainer />} />
            <Route key="update-inbound-ticket" path=":id">
              <Route path="/" element={<InboundTicketContainer />} />
              <Route key="pay-inbound-ticket" path="payment" element={<InboundTicketBillContainer />} />
            </Route>
          </Route>
          <Route path="bills">
            <Route path="/" element={<BillsContainer />} />
            <Route
              key="create-new-bill"
              path="new"
              element={<BillContainer defaultBillType={BILL_TYPES.MATERIAL.id} />}
            />
            <Route key="update-bill" path=":id" element={<BillContainer defaultBillType={BILL_TYPES.MATERIAL.id} />} />
          </Route>
          {enableBatchBills && (
            <Route path="batch-bills">
              <Route path="/" element={<BatchBillsContainer />} />
              <Route key="create-new-batch-bill" path="new" element={<BatchBillContainer />} />
              <Route key="update-batch-bill" path=":id" element={<BatchBillContainer />} />
            </Route>
          )}
        </RouteProtector>

        <RouteProtector
          path="sales"
          element={<ConditionalSalesLayout />}
          isOrgModeRoute={false}
          featureFlag={appRouteSales}
        >
          <Navigate to={getAuthorizedTabs(SALES_TAB_ROUTES)?.[0]?.route || '/dashboard'} replace />
          <Route path="outbound-tickets">
            <Route path="/" element={<OutboundTicketsContainer />} />
            <Route key="create-outbound-ticket" path="new" element={<OutboundTicketContainer />} />
            <Route key="update-outbound-ticket" path=":id" element={<OutboundTicketContainer />} />
          </Route>
          <Route path="invoices">
            <Route path="/" element={<InvoicesContainer />} />
            <Route
              key="create-invoice"
              path="new"
              element={enableInvoiceContainerV2 ? <InvoiceContainerV2 /> : <InvoiceContainer />}
            />
            <Route
              key="update-invoice"
              path=":id"
              element={enableInvoiceContainerV2 ? <InvoiceContainerV2 /> : <InvoiceContainer />}
            />
          </Route>
          <Route path="invoice-payments">
            <Route path="/" element={<InvoicePaymentsContainer />} />
            <Route key="create-invoice-payment" path="new" element={<CreateBulkInvoicePaymentContainer />} />
            <Route key="invoice-payments" path=":id" element={<BulkInvoicePaymentContainer />} />
          </Route>
        </RouteProtector>

        <RouteProtector
          path="contracts"
          element={
            <ContentLayout actions={getAuthorizedActions(CONTRACT_ACTIONS)} title="Contracts">
              <TabsLayout tabs={getAuthorizedTabs(CONTRACT_TAB_ROUTES)} />
            </ContentLayout>
          }
          isOrgModeRoute={false}
          featureFlag={appRouteContracts}
        >
          <Navigate to={getAuthorizedTabs(CONTRACT_TAB_ROUTES)?.[0]?.route || '/dashboard'} replace />
          <Route path="purchase-orders">
            <Route path="/" element={<PurchaseOrdersContainer />} />
            <Route key="create-purchase-order" path="new" element={<EditablePurchaseOrderContainer />} />
            <Route key="view-purchase-order" path=":id" element={<PurchaseOrderContainer />} />
            <Route key="edit-purchase-order" path=":id/edit" element={<EditablePurchaseOrderContainer />} />
          </Route>
          <Route path="sales-orders">
            <Route path="/" element={<SalesOrdersContainer />} />
            <Route key="create-sales-order" path="new" element={<EditableSalesOrderContainer />} />
            <Route key="view-sales-order" path=":id" element={<SalesOrderContainer />} />
            <Route key="edit-sales-order" path=":id/edit" element={<EditableSalesOrderContainer />} />
          </Route>
        </RouteProtector>

        <RouteProtector
          path="inventory"
          element={
            <ContentLayout actions={getAuthorizedActions(INVENTORY_ACTIONS)}>
              <TabsLayout tabs={getAuthorizedTabs(INVENTORY_TAB_ROUTES)} />
            </ContentLayout>
          }
          isOrgModeRoute={false}
          featureFlag={appRouteInventory}
        >
          <Navigate to={getAuthorizedTabs(INVENTORY_TAB_ROUTES)?.[0]?.route || '/dashboard'} replace />
          <Route path="commodities">
            <Route path="/" element={<InventoryCommodityContainer />} />
            <Route path=":commodityId" element={<InventoryCommodityMaterialContainer />} />
          </Route>
          <Route path="regrades">
            <Route path="/:id" element={<RegradesListContainer />} />
            <Route path="/" element={<RegradesListContainer />} />
          </Route>
          <Route path="finished-goods">
            <Route path="/:id" element={<FinishedGoodsListContainer />} />
            <Route path="/" element={<FinishedGoodsListContainer />} />
          </Route>
          <Route path="packing-lists">
            <Route path="/:id" element={<PackingListsContainer />} />
            <Route path="/" element={<PackingListsContainer />} />
          </Route>
          <Route path="adjustments">
            <Route path="/:id" element={<AdjustmentsListContainer />} />
            <Route path="/" element={<AdjustmentsListContainer />} />
          </Route>
          <Route path="negative-inventory">
            <Route path="/" element={<NegativeInventoryListContainer />} />
          </Route>
          <Route path="position" element={<InventoryContainer />} />
        </RouteProtector>

        <RouteProtector
          path="customers"
          element={<ContentLayout actions={customerActions as any} />}
          isOrgModeRoute={false}
          featureFlag={appRouteCustomers}
        >
          <Route path="/" element={<CustomersContainer />} />

          <Route key="customer" path=":id" element={<TabsLayout />}>
            <Navigate to="account" />
            <Route key="customer-account" path="account" element={<CustomerContainer />} />
            <Route key="customer-ticket-history" path="tickets" element={<CustomerTicketsContainer />} />
            <Route key="customer-contract-history" path="contracts" element={<CustomerContractsContainer />} />
            <Route key="customer-locations" path="locations" element={<CustomerLocationsContainer />} />
            <Route key="customer-vehicles" path="vehicles" element={<CustomerVehiclesContainer />} />
            <Route key="customer-contacts" path="contacts" element={<CustomerContactsContainer />} />
            <Route
              key="customer-financial-information"
              path="financial-information"
              element={<CustomerFinancialInformation />}
            />
          </Route>
        </RouteProtector>

        <RouteProtector
          path="commodities"
          element={
            <ContentLayout actions={getAuthorizedActions(COMMODITIES_ACTIONS)}>
              {/commodity-list\/?$|price-lists\/?$/.test(pathname) ? (
                <TabsLayout tabs={getAuthorizedTabs(COMMODITIES_TAB_ROUTES)} />
              ) : null}
            </ContentLayout>
          }
          isOrgModeRoute={false}
          featureFlag={appRouteCommodities}
        >
          <Navigate to={getAuthorizedTabs(COMMODITIES_TAB_ROUTES)?.[0]?.route || '/dashboard'} replace />

          <Route path="/commodity-list">
            <Route path="/" element={<CommoditiesContainer />} />
            <Route path=":commodityId" element={<CommoditiesMaterialContainer />} />
          </Route>

          <Route path="/price-lists">
            <Route path="/" element={<PriceListsContainer />} />
            <Route path=":priceListId" element={<PriceListsContainer />} />
          </Route>

          <Route path="/:commodityId">
            <Navigate to="/app/commodities/commodity-list" />
            <Route path="/:materialName/:materialId" element={<TabsLayout tabs={MATERIAL_PROPERTIES_ROUTES} />}>
              <Navigate to="overview" />
              <Route path="overview" element={<MaterialPropertiesContainer />} />
              <Route path="pricing" element={<MaterialPricingContainer />} />
            </Route>
          </Route>
        </RouteProtector>

        <RouteProtector
          path="freight"
          element={
            <ContentLayout>
              <TabsLayout tabs={getAuthorizedTabs(FREIGHT_TAB_ROUTES)} />
            </ContentLayout>
          }
          isOrgModeRoute={false}
          featureFlag={appRouteFreight}
        >
          <Navigate to={getAuthorizedTabs(FREIGHT_TAB_ROUTES)?.[0]?.route || '/dashboard'} replace />
          <Route path="freight-lanes" element={<FreightLanesContainer />} />
          <Route path="freight-bills">
            <Route path="/" element={<FreightBillsContainer />} />
            <Route
              key="create-new-freight-bill"
              path="new"
              element={<BillContainer defaultBillType={BILL_TYPES.FREIGHT.id} />}
            />
            <Route
              key="update-freight-bill"
              path=":id"
              element={<BillContainer defaultBillType={BILL_TYPES.FREIGHT.id} />}
            />
          </Route>
        </RouteProtector>

        <RouteProtector
          path="load-board"
          element={
            <ContentLayout title="Dispatch Tickets" actions={LOAD_BOARD_ACTIONS}>
              <TabsLayout tabs={LOAD_BOARD_TAB_ROUTES} />
            </ContentLayout>
          }
          isOrgModeRoute
          featureFlag={appRouteLoadBoard}
        >
          <Navigate to="active-tickets" />
          <Route path="active-tickets">
            <Route path="/" element={<DispatchActiveTicketsContainer />} />
          </Route>
          <Route path="ticket-history">
            <Route path="/" element={<DispatchTicketHistoryContainer />} />
            <Route key="create-dispatch-ticket" path="new" element={<DispatchTicketContainer />} />
            <Route key="view-dispatch-ticket" path=":id" element={<DispatchTicketContainer />} />
          </Route>
        </RouteProtector>

        <RouteProtector
          path="assets"
          element={
            <ContentLayout title="Assets" actions={ASSETS_ACTIONS}>
              {/(trucks|trailers|containers|asset-map)\/?$/.test(pathname) ? (
                <TabsLayout tabs={getFilteredTabs(treatments, ASSET_TAB_ROUTES)} />
              ) : null}
            </ContentLayout>
          }
          isOrgModeRoute
          featureFlag={appRouteAssets}
        >
          <Navigate to="asset-map" />
          <Route path="asset-map">
            <Route path="/" element={<AssetMapContainer />} />
          </Route>
          <Route path="trucks">
            <Route path="/" element={<TruckAssetsContainer />} />

            <Route
              key="asset-details-truck"
              path="/:assetId"
              element={
                <TabsLayout
                  tabs={
                    appRouteLoadBoard
                      ? ASSET_DETAIL_TAB_ROUTES
                      : ASSET_DETAIL_TAB_ROUTES.filter(tab => tab.route !== 'history')
                  }
                />
              }
            >
              <Navigate to="profile" />
              <Route path="profile" element={<AssetDetailContainer assetCategory={ASSET_CATEGORY.TRUCK} />} />

              {appRouteLoadBoard && (
                <Route path="history" element={<AssetHistoryContainer assetCategory={ASSET_CATEGORY.TRUCK} />} />
              )}
            </Route>
          </Route>
          <Route path="trailers">
            <Route path="/" element={<TrailerAssetsContainer />} />

            <Route
              key="asset-details-trailers"
              path="/:assetId"
              element={
                <TabsLayout
                  tabs={
                    appRouteLoadBoard
                      ? ASSET_DETAIL_TAB_ROUTES
                      : ASSET_DETAIL_TAB_ROUTES.filter(tab => tab.route !== 'history')
                  }
                />
              }
            >
              <Navigate to="profile" />
              <Route path="profile" element={<AssetDetailContainer assetCategory={ASSET_CATEGORY.TRAILER} />} />

              {appRouteLoadBoard && (
                <Route path="history" element={<AssetHistoryContainer assetCategory={ASSET_CATEGORY.TRAILER} />} />
              )}
            </Route>
          </Route>
          <Route path="containers">
            <Route path="/" element={<ContainerAssetsContainer />} />

            <Route
              key="asset-details-container"
              path="/:assetId"
              element={
                <TabsLayout
                  tabs={
                    appRouteLoadBoard
                      ? ASSET_DETAIL_TAB_ROUTES
                      : ASSET_DETAIL_TAB_ROUTES.filter(tab => tab.route !== 'history')
                  }
                />
              }
            >
              <Navigate to="profile" />
              <Route path="profile" element={<AssetDetailContainer assetCategory={ASSET_CATEGORY.CONTAINER} />} />

              {appRouteLoadBoard && (
                <Route path="history" element={<AssetHistoryContainer assetCategory={ASSET_CATEGORY.CONTAINER} />} />
              )}
            </Route>
          </Route>
        </RouteProtector>

        <RouteProtector
          path="ledger"
          element={
            erpType ? (
              <ContentLayout title="Ledger">
                <TabsLayout tabs={LEDGER_TAB_ROUTES} />
              </ContentLayout>
            ) : undefined
          }
          isOrgModeRoute={false}
          featureFlag={appRouteLedger}
        >
          <Navigate to="sync" />
          <Route key="sync" path="sync" element={erpType ? <SyncContainer /> : undefined} />
          <Route path="sync-log">
            <Route path="/" element={erpType ? <SyncLogContainer /> : undefined} />
            <Route path=":batchId" element={erpType ? <SyncLogContainer /> : undefined} />
          </Route>
        </RouteProtector>

        <RouteProtector
          path="analytics"
          element={
            <ContentLayout actions={ANALYTICS_ACTIONS} title="Analytics">
              <TabsLayout tabs={ANALYTICS_TAB_ROUTES} />
            </ContentLayout>
          }
          isOrgModeRoute={false}
          featureFlag={appRouteAnalytics}
        >
          <Route path="/inbound-ticket-summary" element={<AnalyticsContainer dashboard="inbound-ticket-summary" />} />
          <Route path="/outbound-ticket-summary" element={<AnalyticsContainer dashboard="outbound-ticket-summary" />} />
        </RouteProtector>

        <RouteProtector
          path="reports"
          element={
            enableReportBuilder ? (
              <ContentLayout actions={[]} title="Reports">
                <TabsLayout tabs={getAuthorizedTabs(REPORTS_TAB_ROUTES)} />
              </ContentLayout>
            ) : (
              <ContentLayout actions={[]} title="Reports" />
            )
          }
          isOrgModeRoute
          featureFlag={appRouteReports}
        >
          <Navigate to={getAuthorizedTabs(REPORTS_TAB_ROUTES)?.[0]?.route || '/dashboard'} replace />

          <Route path="standard">
            <Route path="/" element={<ReportsContainer />} />
            <Route key="view-report" path=":id" element={<SingleReportContainer />} />
          </Route>

          <Route path="custom" element={<CustomReportBuilderContainer />} />
        </RouteProtector>

        {isYardAdminOrManager ? (
          <Route path="settings">
            <Navigate to="overview" />
            <RouteProtector
              key="settings-overview"
              path="/overview"
              element={
                <ContentLayout title="Overview">
                  <SettingsContainer layoutType="overview" />
                </ContentLayout>
              }
              isOrgModeRoute={false}
              featureFlag // Feature flag is hard-coded as true here because we're already filtering FF'd routes out above (Settings only)
              isSettingsRoute
            >
              <></>
            </RouteProtector>
            {isYardAdmin && (
              <RouteProtector
                key="settings-users"
                path="/users"
                element={
                  <ContentLayout title="Users" actions={USER_PROVISION_ACTIONS}>
                    <UsersSettingsContainer />
                  </ContentLayout>
                }
                isOrgModeRoute
                featureFlag // can hard code true since this setting left nav option is controlled in SettingsMenuLayout.jsx
                isSettingsRoute
              >
                <></>
              </RouteProtector>
            )}
            {isYardAdmin && (
              <RouteProtector
                path="roles-and-permissions"
                element={<ContentLayout title="Roles And Permissions" />}
                isOrgModeRoute
                featureFlag // can hard code true since this setting left nav option is controlled in SettingsMenuLayout.jsx
                isSettingsRoute
              >
                <Route path="/" element={<RolesAndPermissionsContainer />} />
              </RouteProtector>
            )}
            <RouteProtector
              key="settings-documents"
              path="/documents"
              element={
                <ContentLayout title="Documents">
                  <SettingsContainer layoutType="documents" />
                </ContentLayout>
              }
              isOrgModeRoute={false}
              featureFlag
              isSettingsRoute
            >
              <></>
            </RouteProtector>
            {generateSettingsRoutes('billing', 'Billing', SETTINGS_BILLING_TAB_ROUTES, false)}
            {generateSettingsRoutes('integrations', 'Integrations', SETTINGS_INTEGRATIONS_TAB_ROUTES, false)}
            {generateSettingsRoutes('purchases', 'Purchase Settings', SETTINGS_PURCHASES_TAB_ROUTES, false, treatments)}
            {generateSettingsRoutes('sales', 'Sales Settings', SETTINGS_SALES_TAB_ROUTES, false)}
            {generateSettingsRoutes('contracts', 'Contract Settings', SETTINGS_CONTRACT_TAB_ROUTES, false, treatments)}
            {generateSettingsRoutes('inventory', 'Inventory Settings', SETTINGS_INVENTORY_TAB_ROUTES, false)}
            {generateSettingsRoutes('customers', 'Customer Settings', SETTINGS_CUSTOMERS_TAB_ROUTES, false)}
            {generateSettingsRoutes('commodities', 'Commodity Settings', SETTINGS_COMMODITIES_TAB_ROUTES, false)}
            {generateSettingsRoutes('reports', 'Report Settings', SETTINGS_REPORTS_TAB_ROUTES, false)}
            {generateSettingsRoutes('dispatch', 'Dispatch Settings', SETTINGS_DISPATCH_TAB_ROUTES, true, treatments)}
            {generateSettingsRoutes('ledger', 'Ledger Settings', null, false, treatments)}
            {generateSettingsRoutes('admin', 'Admin Settings', SETTINGS_ADMIN_TAB_ROUTES, false)}
          </Route>
        ) : undefined}

        <Route
          path="*"
          element={<Page404Container homeRoute="/app/purchases" routesAreReady={splitIsReady && roles?.length > 0} />}
        />
      </Route>

      <Route
        path="admin"
        element={withAuthenticator(
          () => (
            <AppLayout
              appBarConfig={{
                showNotifications: false,
                showLocationSelect: true,
                showSettings: !!roles?.find(r => r.roleName === 'yardAdmin'),
                showSupport: true,
                showAccountActions: true,
              }}
              routes={appRoutes}
            />
          ),
          {
            hideSignUp: true,
            components: authModalComponents,
          },
        )({})}
      >
        <Navigate to="organizations" />

        <Route path="organizations" element={<ContentLayout actions={ORGANIZATIONS_ACTIONS} useRouterTitle />}>
          <Route path="/" element={<OrganizationsContainer />} />
          <Route path=":id" element={<SingleOrgContainer />} />
        </Route>

        <Route path="users" element={<div>Users Placeholder</div>} />

        <Route path="monitor" element={<div>Health Monitor Placeholder</div>} />

        <Route path="*" element={<Page404Container homeRoute="/admin" />} />
      </Route>

      <Route path="/" element={<MarketingPageLayout />}>
        <Route element={<LandingPage />} />
        <Route path="about-us" element={<AboutUs />} />
        <Route path="careers" element={<Careers />} />
      </Route>

      <Route path="*" element={<Page404Container />} />
    </Routes>
  )
}

export default AppRoutes
